import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Button, Spinner } from 'react-bootstrap';
import moment from 'moment';
import 'moment/locale/es';
import { actions as timesheetActions, selectors as timesheetSelectors } from '../../reducers/timesheet';
import './HorasPersonas.css';

moment.locale('es');

/**
 * Reporte de horas registradas por persona (y proyecto) para un mes.
 * Punto 20 + 21 del plan:
 *  - Vista en proyecto: personas x día.
 *  - Vista en programa: personas x proyecto x día (tabla global).
 *  - Celdas con más de 8h/día resaltadas en rojo.
 */
function HorasPersonas({ proyectoIds = [], programaId = null, titulo = 'Horas registradas', rows, loading, dispatch }) {
    const [currentDate, setCurrentDate] = useState(moment());

    const { startDate, endDate, daysArray } = useMemo(() => {
        const start = currentDate.clone().startOf('month');
        const end = currentDate.clone().endOf('month');
        const days = [];
        let curr = start.clone();
        while (curr.isSameOrBefore(end, 'day')) {
            days.push(curr.clone());
            curr.add(1, 'day');
        }
        return { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD'), daysArray: days };
    }, [currentDate]);

    const showProyecto = programaId || (proyectoIds || []).length > 1;

    // Clave estable de ids para no refetch en cada re-render del padre
    const idsKey = useMemo(
        () => (Array.isArray(proyectoIds) ? proyectoIds.join(',') : String(proyectoIds || '')),
        [proyectoIds]
    );

    useEffect(() => {
        const ids = idsKey ? idsKey.split(',').map(n => parseInt(n, 10)).filter(n => !isNaN(n)) : [];
        dispatch(timesheetActions.fetchHoras({ proyectoIds: ids, programaId, desde: startDate, hasta: endDate }));
    }, [dispatch, idsKey, programaId, startDate, endDate]);

    // Sacar filas agregadas por (persona [+ proyecto]) y mapa clave -> horas por día
    const { rowsList, dayTotals, totalGeneral } = useMemo(() => {
        const byKey = {};
        const dayTotalsMap = {};

        (rows || []).forEach(r => {
            const key = showProyecto ? `u${r.usuario_id}p${r.project_id}` : `u${r.usuario_id}`;
            if (!byKey[key]) {
                byKey[key] = {
                    usuario_id: r.usuario_id,
                    nombre: r.usuario_nombre,
                    correo: r.correo,
                    project_id: r.project_id,
                    proyecto_nombre: r.proyecto_nombre,
                    cells: {},
                };
            }
            byKey[key].cells[r.fecha] = r.horas;
            dayTotalsMap[r.fecha] = (dayTotalsMap[r.fecha] || 0) + r.horas;
        });

        const rowList = Object.values(byKey).map(row => ({
            ...row,
            total: Object.values(row.cells).reduce((s, h) => s + h, 0),
        }));

        const total = rowList.reduce((s, row) => s + row.total, 0);

        return { rowsList: rowList, dayTotals: dayTotalsMap, totalGeneral: total };
    }, [rows, showProyecto]);

    const handlePrev = () => setCurrentDate(prev => prev.clone().subtract(1, 'month'));
    const handleNext = () => setCurrentDate(prev => prev.clone().add(1, 'month'));
    const handleToday = () => setCurrentDate(moment());

    return (
        <div className="horas-personas">
            <div className="horas-personas-header d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                <div>
                    <h5 className="horas-personas-title mb-1">
                        <i className="bi bi-bar-chart me-2" />
                        {titulo}
                    </h5>
                    <small className="text-muted">
                        {showProyecto
                            ? 'Celdas con más de 8 horas en el día se marcan en rojo. Un mismo usuario puede aparecer por proyecto.'
                            : 'Celdas con más de 8 horas en el día se marcan en rojo.'}
                    </small>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Button variant="outline-primary" size="sm" onClick={handlePrev} title="Mes anterior">
                        <i className="bi bi-chevron-left" />
                    </Button>
                    <span className="horas-personas-period-label text-capitalize">{currentDate.format('MMMM YYYY')}</span>
                    <Button variant="outline-primary" size="sm" onClick={handleNext} title="Mes siguiente">
                        <i className="bi bi-chevron-right" />
                    </Button>
                    <Button variant="light" size="sm" onClick={handleToday} className="ms-1 fw-semibold">
                        Hoy
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="text-muted mt-2">Cargando horas registradas...</p>
                </div>
            ) : rowsList.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    <i className="bi bi-calendar-x" style={{ fontSize: '2.5rem' }} />
                    <h6 className="mt-3">No hay horas registradas en este periodo</h6>
                    <p className="small mb-0">Los integrantes que registren horas verán aquí el detalle por día.</p>
                </div>
            ) : (
                <div className="horas-personas-table-container table-responsive">
                    <table className="horas-personas-table table table-sm table-hover table-bordered">
                        <thead>
                            <tr className="horas-personas-thead">
                                <th className="horas-personas-th-user">
                                    Persona
                                </th>
                                {showProyecto && (
                                    <th className="horas-personas-th-proyecto">Proyecto</th>
                                )}
                                {daysArray.map((d) => {
                                    const dStr = d.format('YYYY-MM-DD');
                                    const isWeekend = d.isoWeekday() > 5;
                                    const isToday = d.isSame(moment(), 'day');
                                    return (
                                        <th
                                            key={dStr}
                                            className={`horas-personas-th-day ${isWeekend ? 'horas-weekend' : ''} ${isToday ? 'horas-today' : ''}`}
                                        >
                                            <div className="horas-day-name">{d.format('ddd')}</div>
                                            <div className="horas-day-number">{d.format('D')}</div>
                                            <div className="horas-day-total">{dayTotals[dStr] > 0 ? `${dayTotals[dStr].toFixed(1)}h` : ''}</div>
                                        </th>
                                    );
                                })}
                                <th className="horas-personas-th-total text-center">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rowsList.map(row => {
                                const rowKey = showProyecto ? `u${row.usuario_id}p${row.project_id}` : `u${row.usuario_id}`;
                                return (
                                    <tr key={rowKey} className="horas-row-persona">
                                        <td className="horas-td-user">
                                            <div className="horas-user-name">{row.nombre}</div>
                                            {row.correo && <div className="horas-user-email small text-muted">{row.correo}</div>}
                                        </td>
                                        {showProyecto && (
                                            <td className="horas-td-proyecto">
                                                <span className="horas-proyecto-badge">{row.proyecto_nombre}</span>
                                            </td>
                                        )}
                                        {daysArray.map((d) => {
                                            const dStr = d.format('YYYY-MM-DD');
                                            const isWeekend = d.isoWeekday() > 5;
                                            const horas = row.cells[dStr];
                                            const hayHoras = horas && horas > 0;
                                            const over = hayHoras && horas > 8;
                                            return (
                                                <td
                                                    key={dStr}
                                                    className={`horas-td-cell text-center ${isWeekend ? 'horas-weekend' : ''} ${hayHoras ? 'horas-filled' : ''} ${over ? 'horas-over' : ''}`}
                                                    title={over ? 'Más de 8 horas registradas en el día' : `Horas de ${row.nombre} ${d.format('DD/MM')}`}
                                                >
                                                    {hayHoras ? horas.toFixed(1) : ''}
                                                </td>
                                            );
                                        })}
                                        <td className="horas-td-total text-center fw-bold">
                                            {row.total.toFixed(1)}h
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="horas-footer">
                                <td className="horas-footer-total" colSpan={showProyecto ? 2 : 1}>
                                    Total general
                                </td>
                                {daysArray.map((d) => {
                                    const dStr = d.format('YYYY-MM-DD');
                                    const total = dayTotals[dStr] || 0;
                                    return (
                                        <td key={dStr} className="horas-footer-day text-center">
                                            {total > 0 ? total.toFixed(1) : ''}
                                        </td>
                                    );
                                })}
                                <td className="horas-footer-total text-center">{totalGeneral.toFixed(1)}h</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}

const mapStateToProps = (state) => ({
    rows: timesheetSelectors.getHorasRows(state),
    loading: timesheetSelectors.getHorasLoading(state),
});

export default connect(mapStateToProps)(HorasPersonas);